import React, { useEffect, useState } from 'react';
import { Shield, Check } from 'lucide-react';
import api from '../services/api';
import { PageHeader } from '../components/PageParts';
import { toast } from 'sonner';

const MODULE_LABELS = {
  dashboard: 'Dashboard', clients: 'Clientes', fleet: 'Frota', financial: 'Financeiro',
  maintenance: 'Manutenção', hr: 'RH', stock: 'Estoque', reports: 'Relatórios',
  settings: 'Configurações', users: 'Usuários', roles: 'Perfis', company: 'Empresa',
};

const ACTION_LABELS = { view: 'Visualizar', create: 'Cadastrar', edit: 'Editar', delete: 'Excluir', export: 'Exportar' };

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([api.get('/roles'), api.get('/roles/modules')]);
      setRoles(r.data);
      setModules(m.data.modules);
      setActions(m.data.actions);
      if (!selected && r.data.length) setSelected(r.data[0]);
    } catch { toast.error('Erro ao carregar perfis'); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const togglePermission = async (module, action) => {
    if (!selected) return;
    const current = selected.permissions?.[module] || [];
    const next = current.includes(action) ? current.filter(a => a !== action) : [...current, action];
    const newPerms = { ...(selected.permissions || {}), [module]: next };
    try {
      const { data } = await api.put(`/roles/${selected.id}`, { permissions: newPerms });
      setSelected(data);
      setRoles(roles.map(r => r.id === data.id ? data : r));
      toast.success('Permissão atualizada');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  return (
    <div>
      <PageHeader title="Perfis e Permissões" subtitle="Configure o acesso de cada perfil aos módulos do sistema" />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Lista de perfis */}
        <div className="lg:col-span-1 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-3 h-fit">
          <p className="text-xs uppercase tracking-wider text-neutral-500 px-2 pt-1 pb-2">Perfis</p>
          <div className="space-y-1">
            {loading ? Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-11 rounded-lg shimmer" />) :
              roles.map(r => (
                <button key={r.id} onClick={() => setSelected(r)} className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${selected?.id === r.id ? 'bg-brand text-white' : 'hover:bg-neutral-100 dark:hover:bg-neutral-800'}`}>
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    <div className="flex-1">
                      <div className="font-medium">{r.name}</div>
                      <div className={`text-[11px] ${selected?.id === r.id ? 'text-red-100' : 'text-neutral-500'}`}>{r.description || 'Sem descrição'}</div>
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </div>

        {/* Matriz de permissões */}
        <div className="lg:col-span-3 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
          {selected ? (
            <>
              <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
                <h2 className="font-semibold">Perfil: <span className="text-brand">{selected.name}</span></h2>
                <p className="text-xs text-neutral-500">Marque as permissões disponíveis para cada módulo.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium">Módulo</th>
                      {actions.map(a => <th key={a} className="px-3 py-3 text-center font-medium">{ACTION_LABELS[a]}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                    {modules.map(mod => (
                      <tr key={mod} className="tr-hover">
                        <td className="px-4 py-2.5 font-medium">{MODULE_LABELS[mod] || mod}</td>
                        {actions.map(act => {
                          const has = (selected.permissions?.[mod] || []).includes(act);
                          return (
                            <td key={act} className="px-3 py-2.5 text-center">
                              <button onClick={() => togglePermission(mod, act)} disabled={selected.name === 'Administrador'} className={`h-6 w-6 rounded-md flex items-center justify-center mx-auto transition-colors ${has ? 'bg-brand text-white' : 'bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700'} ${selected.name === 'Administrador' ? 'opacity-70 cursor-not-allowed' : ''}`}>
                                {has && <Check className="h-4 w-4" />}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {selected.name === 'Administrador' && (
                <div className="px-5 py-3 border-t border-neutral-200 dark:border-neutral-800 text-xs text-neutral-500">
                  O perfil Administrador possui acesso total e não pode ser modificado.
                </div>
              )}
            </>
          ) : (
            <div className="p-10 text-center text-neutral-500">Selecione um perfil</div>
          )}
        </div>
      </div>
    </div>
  );
}
